import React, { ReactNode, useCallback, useState } from 'react';
import { AppShell, Group, Text, ActionIcon, Button, ScrollArea, Alert, TextInput } from '@mantine/core';
import { IconSearch, IconUser, IconArrowBarToLeft, IconArrowBarToRight, IconEdit, IconMessageCircle, IconGrid3x3, IconAlertCircle, IconCode, IconRobot, IconBell, IconX } from '@tabler/icons-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useConversations, useNavbarExpansion } from '../../../hooks';
import { conversationUtils } from '../../../api';
import { useConversationStore } from '../../../stores/conversationStore';
import { useShallow } from 'zustand/react/shallow';
import { ConversationItem } from './components/ConversationItem';
import styles from './AppShell.module.css';
import { NAVBAR_FEATURES } from '../../../config/featureFlags';
import { useIsMessageSending } from '../../../hooks';
import { trackComponentInteraction } from '../../../utils/analytics';
import { NotificationRoot, showNotification } from '../../Notification';


export interface AppShellProps {
  children: ReactNode;
}

export function AppShellLayout({ children }: AppShellProps) {
  const { isExpanded: desktopOpened, toggleExpanded: toggleDesktop, isNavBarLocked, setNavBarLocked } = useNavbarExpansion();
  const { isMobileNavbarOpen, setMobileNavbarOpen, toggleMobileNavbar } = useConversationStore(useShallow((state) => ({
    isMobileNavbarOpen: state.isMobileNavbarOpen,
    setMobileNavbarOpen: state.setMobileNavbarOpen,
    toggleMobileNavbar: state.toggleMobileNavbar,
  })));
  const navigate = useNavigate();
  const routerState = useRouterState();

  const handleNavbarToggle = () => {
    trackComponentInteraction(
      'AppShell',
      'Navigation Sidebar',
      'navbar',
      'toggle',
      desktopOpened ? 'collapse' : 'expand'
    );
    toggleDesktop();
  };

  const handleNavBarMouseEnter = () => {
    if (!isNavBarLocked) {
      toggleDesktop();
    }
  }

  const handleNavBarMouseLeave = () => {
    if (!isNavBarLocked) {
      toggleDesktop();
    }
  };

  const toggleDesktopLock = () => {
    if (isNavBarLocked) {
      toggleDesktop();
    }
    setNavBarLocked(!isNavBarLocked);
  };

  // Get conversation data
  const {
    conversations,
    groupedConversations,
    isLoading,
    isError,
    error,
    setSelectedConversation,
    refetch,
  } = useConversations();

  // Search state from store (useConversations already applies filtering before grouping)
  const searchTerm = useConversationStore((state) => state.searchTerm);
  const setSearchTerm = useConversationStore((state) => state.setSearchTerm);
  const clearSearch = useConversationStore((state) => state.clearSearch);

  // Get conversation store state
  const {
    currentConversationId,
    setCurrentConversationId
  } = useConversationStore(useShallow((state) => ({
    currentConversationId: state.currentConversationId,
    setCurrentConversationId: state.setCurrentConversationId,
  })));

  // Check if any message is currently being sent across the entire app
  const isMessageSending = useIsMessageSending();

  // Extract current conversation ID from route
  const routeConversationId = routerState.location.pathname.includes('/chat/')
    ? routerState.location.pathname.split('/chat/')[1]
    : null;

  const closeMobileNav = useCallback(() => setMobileNavbarOpen(false), [setMobileNavbarOpen]);

  const handleNewChat = () => {
    clearSearch();
    // Clear current conversation state
    setCurrentConversationId(null);
    setSelectedConversation(null);

    // Navigate to home
    navigate({ to: '/' });
    // Auto-close mobile navbar after navigation
    closeMobileNav();
  };

  const handleChatClick = useCallback((conversationId: string) => {
    clearSearch();
    // Update conversation store state
    setSelectedConversation(conversationId);
    setCurrentConversationId(conversationId);

    // Navigate to conversation
    navigate({
      to: '/chat/$conversationId',
      params: { conversationId }
    });
    // Auto-close mobile navbar after navigation
    closeMobileNav();
  }, [setSelectedConversation, setCurrentConversationId, navigate, closeMobileNav]);

  // Removed loading conversation tracking - let individual routes handle loading states

  const handleDevNavigation = () => {
    // Navigate to sample conversation for development
    navigate({
      to: '/chat/$conversationId',
      params: { conversationId: 'dev-sample-conversation' }
    });
    // Auto-close mobile navbar after navigation
    closeMobileNav();
  };

  const handleAgentsNavigation = () => {
    clearSearch();
    trackComponentInteraction('Agents', 'Select Agents', 'Button', 'click', 'agents', '/agents')
    navigate({ to: '/agents' });
    // Auto-close mobile navbar after navigation
    closeMobileNav();
  };

  // Show more/less state for pinned conversations
  const [showAllPinned, setShowAllPinned] = useState(false);

  // Memoized conversation group rendering for performance
  const renderConversationGroup = useCallback((title: string, conversations: typeof groupedConversations.today) => {
    if (conversations.length === 0) return null;

    return (
      <div key={title}>
        <Text
          size="sm"
          c="rgba(255,255,255,0.5)"
          mb="sm"
          mt={title !== 'Pinned' ? 'lg' : undefined}
          className={styles.conversationHeader}
        >
          {title}
        </Text>
        {conversations.map((conversation) => {
          const isActive = currentConversationId === conversation.conversationID ||
                         routeConversationId === conversation.conversationID;

          return (
            <ConversationItem
              key={conversation.conversationID}
              conversation={conversation}
              isActive={isActive}
              onChatClick={handleChatClick}
            />
          );
        })}
      </div>
    );
  }, [currentConversationId, routeConversationId, handleChatClick]);

  // Dedicated renderer for Pinned group with show more/hide functionality
  const renderPinnedConversationGroup = useCallback(() => {
    const pinned = groupedConversations.pinned;
    if (!pinned || pinned.length === 0) return null;

    const pinPreviewLimit = 3

    const displayed = showAllPinned ? pinned : pinned.slice(0, pinPreviewLimit);

    return (
      <>
        {renderConversationGroup('Pinned', displayed)}
        {pinned.length > pinPreviewLimit && (
          <Button
            size="xs"
            variant="subtle"
            fullWidth
            mt="xs"
            onClick={() => setShowAllPinned((prev) => !prev)}
            style={{ '--button-justify': 'left', '--button-padding-x': 'var(--mantine-spacing-sm)' }}
            className={styles.showMore}
          >
            <Text c="rgba(255,255,255,0.5)" size="sm">
              {showAllPinned ? 'Hide' : 'Show +'}
            </Text>
          </Button>
        )}
      </>
    );
  }, [groupedConversations.pinned, showAllPinned, renderConversationGroup]);

  // Memoized conversation list rendering for performance
  const renderConversationList = useCallback(() => {
    if (isLoading) {
      return (
        <Text size="sm" c="rgba(255,255,255,0.5)" ta="center" mt="lg">
          Loading conversations...
        </Text>
      );
    }

    if (isError) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error loading conversations"
          color="red"
          variant="light"
          mb="md"
        >
          <Text size="sm">{error || 'Failed to load conversations'}</Text>
          <Button size="xs" variant="light" color="red" mt="xs" onClick={refetch}>
            Retry
          </Button>
        </Alert>
      );
    }

    if (conversations.length === 0) {
      return (
        <Text size="sm" c="rgba(255,255,255,0.5)" ta="center" mt="lg">
          No conversations yet
        </Text>
      );
    }

    // When searching, show a dedicated empty state if no matches across all groups
    const totalMatches = groupedConversations.pinned.length +
      groupedConversations.today.length +
      groupedConversations.yesterday.length +
      groupedConversations.lastWeek.length +
      groupedConversations.older.length;

    if (searchTerm.trim() && totalMatches === 0) {
      return (
        <Text size="sm" c="rgba(255,255,255,0.5)" ta="center" mt="lg">
          No matching conversations
        </Text>
      );
    }

    // Use simplified grouping per Figma design
    if (NAVBAR_FEATURES.showTimeGrouping) {
      return (
        <>
          {renderPinnedConversationGroup()}
          {renderConversationGroup('Today', groupedConversations.today)}
          {renderConversationGroup('Yesterday', groupedConversations.yesterday)}
          {renderConversationGroup('Last Week', groupedConversations.lastWeek)}
          {renderConversationGroup('Older', groupedConversations.older)}
        </>
      );

    } else if (NAVBAR_FEATURES.showPinnedChats) {
      // Simplified date grouping - single "Last Updated" header, but keep pins
      const allSortedConversations = [
        ...groupedConversations.today,
        ...groupedConversations.yesterday,
        ...groupedConversations.lastWeek,
        ...groupedConversations.older
      ];
      return (
        <>
          {renderPinnedConversationGroup()}
          {renderConversationGroup('Last Updated', allSortedConversations)}
        </>
      );

    } else {
      // Simplified grouping - single "Last Updated" header
      // Use sorted conversations from groupedConversations to ensure newest first ordering
      const allSortedConversations = [
        ...groupedConversations.pinned,
        ...groupedConversations.today,
        ...groupedConversations.yesterday,
        ...groupedConversations.lastWeek,
        ...groupedConversations.older
      ];

      return (
        <>
          {renderConversationGroup('Last Updated', allSortedConversations)}
        </>
      );
    }
  }, [isLoading, isError, error, refetch, conversations.length, renderConversationGroup, groupedConversations, renderPinnedConversationGroup, searchTerm]);

  const isExpandedOrMobileOpen = desktopOpened || isMobileNavbarOpen;

  return (
    <AppShell
      navbar={{
        width: isExpandedOrMobileOpen ? 300 : 78,
        breakpoint: 'sm',
        collapsed: { mobile: !isMobileNavbarOpen, desktop: false },
      }}
      classNames={{
        root: styles.appShell,
        navbar: styles.navbar,
        main: styles.main,
      }}
    >
      <AppShell.Navbar
        p={isExpandedOrMobileOpen ? "md" : "18px"}
        onMouseEnter={handleNavBarMouseEnter}
        onMouseLeave={handleNavBarMouseLeave}
      >
        {isExpandedOrMobileOpen ? (
          // Expanded state - full navbar with text labels
          <>
            <AppShell.Section>
              <Group justify="space-between" mb="lg" className={styles.navLogoContainer}>
                <Group gap="xs">
                  <div className={`${styles.logoFrame} ${styles.logoFrameOpen}`}>
                    <IconRobot size={24} color="white" aria-label="Co-op GPT Bot" />
                  </div>
                  <Text size="md" fw={600} c="white">Co-op GPT</Text>
                </Group>
                <ActionIcon
                  size={42}
                  radius={6}
                  variant="subtle"
                  onClick={closeMobileNav}
                  hiddenFrom="sm"
                  aria-label="Close navigation"
                  classNames={{ root: styles.navButton }}
                >
                  <IconX size={24} color="white" />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  onClick={toggleDesktopLock}
                  visibleFrom="sm"
                  classNames={{
                    root: styles.collapseButton
                  }}
                >
                  {isNavBarLocked ? (
                      <IconArrowBarToLeft size={20} color="white" />
                    ) : (
                      <IconArrowBarToRight size={20} color="white" />
                    )
                  }
                </ActionIcon>
              </Group>
            </AppShell.Section>

            <AppShell.Section className={styles.navOpenItems}>
              {NAVBAR_FEATURES.showNewChat && (
                <Button
                  leftSection={<IconEdit size={24} />}
                  variant="subtle"
                  justify="flex-start"
                  fullWidth
                  mb="sm"
                  onClick={handleNewChat}
                  disabled={isMessageSending}
                  classNames={{
                    root: styles.navButton
                  }}
                >
                  New Chat
                </Button>
              )}

              {NAVBAR_FEATURES.showSearch && (
                <TextInput
                  leftSection={<IconSearch size={24} />}
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.currentTarget.value)}
                  rightSection={searchTerm ? (
                    <ActionIcon className={styles.navSearchClearButton} size={36} variant="subtle" onClick={clearSearch} aria-label="Clear search">
                      <IconX strokeWidth={1.4} size={36} />
                    </ActionIcon>
                  ) : undefined}
                  classNames={{
                    root: `${styles.navButton} ${styles.navSearch}`,
                    section: styles.navSearchSection,
                    wrapper: styles.navSearchWrapper,
                    input: styles.navSearchInner,
                  }}
                />
              )}

              {NAVBAR_FEATURES.showAgents && (
                <Button
                  leftSection={<IconUser size={24} />}
                  variant="subtle"
                  justify="flex-start"
                  fullWidth
                  mb="sm"
                  onClick={handleAgentsNavigation}
                  disabled={isMessageSending}
                  classNames={{
                    root: styles.navButton
                  }}
                >
                  Agents
                </Button>
              )}

              {NAVBAR_FEATURES.showDevChat && (
                <Button
                  leftSection={<IconCode size={24} />}
                  variant="subtle"
                  justify="flex-start"
                  fullWidth
                  mb="lg"
                  onClick={handleDevNavigation}
                  classNames={{
                    root: styles.navButton
                  }}
                >
                  Dev Chat
                </Button>
              )}
            </AppShell.Section>

            {(NAVBAR_FEATURES.showChats || NAVBAR_FEATURES.showPersonas) && (
              <AppShell.Section>
                {NAVBAR_FEATURES.showChats && (
                  <Button
                    leftSection={<IconMessageCircle size={24} />}
                    variant="subtle"
                    justify="flex-start"
                    fullWidth
                    mb="xs"
                    classNames={{
                      root: styles.navButton
                    }}
                  >
                    Chats
                  </Button>
                )}

                {NAVBAR_FEATURES.showPersonas && (
                  <Button
                    leftSection={<IconGrid3x3 size={24} />}
                    variant="subtle"
                    justify="flex-start"
                    fullWidth
                    mb="lg"
                    classNames={{
                      root: styles.navButton
                    }}
                  >
                    Personas
                  </Button>
                )}
              </AppShell.Section>
            )}

            <AppShell.Section grow component={ScrollArea}>
              {renderConversationList()}
            </AppShell.Section>
          </>
        ) : (
          // Collapsed state - icon-only navbar matching Figma design
          <>
            <AppShell.Section className={styles.collapsedSection}>
              <ActionIcon
                size={42}
                radius={6}
                variant="subtle"
                onClick={closeMobileNav}
                hiddenFrom="sm"
                aria-label="Close navigation"
                classNames={{ root: styles.navButton }}
              >
                <IconX size={24} color="white" />
              </ActionIcon>
              {/* Logo */}
              <ActionIcon
                size={42}
                radius={6}
                variant="subtle"
                onClick={toggleDesktop}
                className={styles.logoFrame}
              >
                <IconRobot size={24} color="white" aria-label="Co-op GPT Bot" />
              </ActionIcon>

              {/* Nav Group 1: New Chat, Agents, Search */}
              {(NAVBAR_FEATURES.showNewChat || NAVBAR_FEATURES.showAgents || NAVBAR_FEATURES.showSearch || NAVBAR_FEATURES.showDevChat) && (
                <div className={styles.navGroup}>
                  {NAVBAR_FEATURES.showNewChat && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      onClick={handleNewChat}
                      disabled={isMessageSending}
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconEdit size={24} color="white" />
                    </ActionIcon>
                  )}
                  {NAVBAR_FEATURES.showSearch && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconSearch size={24} color="white" />
                    </ActionIcon>
                  )}
                  {NAVBAR_FEATURES.showAgents && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      onClick={handleAgentsNavigation}
                      disabled={isMessageSending}
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconUser size={24} color="white" />
                    </ActionIcon>
                  )}
                  {NAVBAR_FEATURES.showDevChat && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      onClick={handleDevNavigation}
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconCode size={24} color="white" />
                    </ActionIcon>
                  )}
                </div>
              )}

              {/* Nav Group 2: Chats, Personas */}
              {(NAVBAR_FEATURES.showChats || NAVBAR_FEATURES.showPersonas) && (
                <div className={styles.navGroup}>
                  {NAVBAR_FEATURES.showChats && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconMessageCircle size={24} color="white" />
                    </ActionIcon>
                  )}
                  {NAVBAR_FEATURES.showPersonas && (
                    <ActionIcon
                      size={42}
                      radius={6}
                      variant="subtle"
                      classNames={{
                        root: styles.navButton
                      }}
                    >
                      <IconGrid3x3 size={24} color="white" />
                    </ActionIcon>
                  )}
                </div>
              )}
            </AppShell.Section>
          </>
        )}
      </AppShell.Navbar>

      <AppShell.Main
        classNames={{
          main: styles.main
        }}
      >
        {/* Global notification popup */}
        <NotificationRoot />

        {children}
      </AppShell.Main>
    </AppShell>
  );
}
